import { allControls, hotkeyPrefix } from '@shared/constants/CommandValues';
import { getKeyDisplayname } from '@shared/constants/Keybindings';
import KeyCommandMapping, { KeyCommandDisplayMapping } from '@shared/models/deviceProfiles/KeyCommandMapping';
import { MacroDisplayEvent, MacroEventType, macroEventTypes } from '@shared/models/deviceProfiles/MacroEvent';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import {
    CommandConflictDisplayModel,
    ConflictDetail,
    ProfileConflictItem,
    ProfileConflictModel,
} from '@shared/models/profiles/ProfileConflictModel';
import { CommonResourceKey } from '@shared/models/ResourceKeys';
import { TFunction } from 'i18next';

interface ConflictingVoiceCommandsParams {
    macros: MacroDisplayModel[];
    dictationCommands: string[];
    profileId: string | undefined;
    t: TFunction;
}
interface ConflictingKeybindingVoiceCommandsParams {
    commandKeyMappings: KeyCommandDisplayMapping[];
    dictationCommands: string[];
    profileId: string | undefined;
    t: TFunction;
}
interface GetDuplicateKeybindingCommandsParams {
    commandKeyMappings: KeyCommandDisplayMapping[];
    profileId: string | undefined;
    t: TFunction;
}
interface GetDuplicateAssociatedProgramsParams {
    associatedPrograms: string[];
    profileId: string | undefined;
}
interface IMacroValidation {
    macros: MacroDisplayModel[];
    profileId: string | undefined;
    t: TFunction;
}

interface GetUnreleasedLatchedKeysParams {
    macros: MacroDisplayModel[];
    profileId: string | undefined;
    defaultLocale: string;
    t: TFunction;
}
interface GetEmptyKeybindingCommandsParams {
    commandKeyMappings: KeyCommandDisplayMapping[];
    profileId: string | undefined;
    t: TFunction;
}

/**
 * VALIDATION
 */

export const getConflictingMacroVoiceCommandsWithDictation = (
    params: ConflictingVoiceCommandsParams
): ProfileConflictItem[] => {
    const { macros, dictationCommands, profileId, t } = params;
    const macrosWithVoiceCommands = macros.filter((m) =>
        m.commands.some((c) => c.commandType === 'voice' && c.command !== '')
    );
    const nonEmptyDictationCommands = dictationCommands.filter((c) => c !== '').map((c) => c.toLowerCase());

    const conflictingVoiceCommands: ProfileConflictItem[] = [];

    macrosWithVoiceCommands.forEach((macro) => {
        macro.commands.forEach((c) => {
            if (nonEmptyDictationCommands.includes(c.command.toLowerCase())) {
                const macroName = macro.name ?? getMacroFallbackName(c.command, t);

                conflictingVoiceCommands.push({
                    key: 'MacroVoiceCommandWithDictation',
                    scrollID: macro.id,
                    displayString: getConflictIndicatorDisplayString(macroName, c.command, c.commandType, t),
                    pageLink: `/profiles/edit/${profileId}`,
                });
            }
        });
    });

    return conflictingVoiceCommands;
};

export const getConflictingKeybindingsVoiceCommandsWithDictation = (
    params: ConflictingKeybindingVoiceCommandsParams
): ProfileConflictItem[] => {
    const { commandKeyMappings, dictationCommands, profileId, t } = params;

    const keybindingsWithVoiceCommands = commandKeyMappings.filter((c) =>
        c.commands.some((c) => c.commandType === 'voice' && c.command !== '')
    );
    const nonEmptyDictationCommands = dictationCommands.filter((c) => c !== '').map((c) => c.toLowerCase());

    const conflictingVoiceCommands: ProfileConflictItem[] = [];

    keybindingsWithVoiceCommands.forEach((keybinding) => {
        keybinding.commands.forEach((c) => {
            if (nonEmptyDictationCommands.includes(c.command.toLowerCase())) {
                conflictingVoiceCommands.push({
                    key: 'KeybindingVoiceCommandWithDictation',
                    scrollID: keybinding.key,
                    displayString: getConflictIndicatorDisplayString(
                        keybinding.displayName,
                        c.command,
                        c.commandType,
                        t
                    ),
                    pageLink: `/profiles/edit/${profileId}/advanced/keybindings`,
                });
            }
        });
    });

    return conflictingVoiceCommands;
};

export const getDuplicateKeybindingCommands = (params: GetDuplicateKeybindingCommandsParams): ProfileConflictItem[] => {
    const { commandKeyMappings, profileId, t } = params;
    const commands: CommandConflictDisplayModel[] = [];

    commandKeyMappings.forEach((keyCommand) => {
        const nonEmptyCommands = keyCommand.commands.filter((c) => c.command !== '');
        const transformedCommands = nonEmptyCommands.map((commandModel) => {
            let commandName = getCommandDisplayName(commandModel.command, t);
            if (commandModel.commandType === 'voice') {
                commandName = commandName.toLowerCase();
            }

            return {
                commandDisplayName: commandName,
                commandType: commandModel.commandType,
                macroOrKeyName: keyCommand.displayName,
                macroOrKeyId: keyCommand.key,
                pageLink: `/profiles/edit/${profileId}/advanced/keybindings`,
            };
        });
        commands.push(...transformedCommands);
    });

    return getDuplicateCommands(commands, 'DuplicateKeybindingInputCommands', t);
};

export const getDuplicateCommands = (
    allCommands: CommandConflictDisplayModel[],
    key: string,
    t: TFunction
): ProfileConflictItem[] => {
    const alreadySeen: { [inputName_controlType: string]: ConflictDetail[] } = {};
    const duplicated: ProfileConflictItem[] = [];

    // Create a map of command input names and control type to a list of macros or keybindings
    allCommands.forEach((commandModel) => {
        const keyStr = `${commandModel.commandDisplayName}_${commandModel.commandType}`;
        if (!alreadySeen[keyStr]) {
            alreadySeen[keyStr] = [];
        }
        alreadySeen[keyStr].push({
            id: commandModel.macroOrKeyId,
            name: commandModel.macroOrKeyName,
            pageLink: commandModel.pageLink,
        });
    });

    // Create the duplicate conflicting commands list
    for (const commandModelKey in alreadySeen) {
        if (alreadySeen[commandModelKey].length > 1) {
            const [inputName, controlType] = commandModelKey.split('_');

            alreadySeen[commandModelKey].forEach((conflictDetail) => {
                duplicated.push({
                    key,
                    scrollID: conflictDetail.id,
                    displayString: getConflictIndicatorDisplayString(conflictDetail.name, inputName, controlType, t),
                    pageLink: conflictDetail.pageLink,
                });
            });
        }
    }

    return duplicated;
};

export const getDuplicateAssociatedPrograms = (params: GetDuplicateAssociatedProgramsParams): ProfileConflictItem[] => {
    const { associatedPrograms, profileId } = params;
    const uniqueNames = new Set<string>();
    const duplicatedNames: ProfileConflictItem[] = [];

    associatedPrograms.forEach((name, idx) => {
        const lowerCaseName = name.toLowerCase();
        if (lowerCaseName === '') {
            return;
        }

        if (uniqueNames.has(lowerCaseName)) {
            duplicatedNames.push({
                displayString: name,
                key: 'DuplicateAssociatedProgramsName',
                pageLink: `/profiles/edit/${profileId}`,
                scrollID: `${idx}_associated_program_field`,
            });
        } else {
            uniqueNames.add(lowerCaseName);
        }
    });

    return duplicatedNames;
};

export const getEmptyMacroCommands = (params: IMacroValidation): ProfileConflictItem[] => {
    const { macros, profileId, t } = params;
    const emptyCommands: ProfileConflictItem[] = [];
    const emptyCommandName = t('common:noName');

    macros.forEach((macro) => {
        macro.commands.forEach((c) => {
            if (!c.command) {
                const macroName = macro.name ?? t('common:noName');
                emptyCommands.push({
                    key: 'EmptyMacroInputs',
                    scrollID: macro.id,
                    displayString: getConflictIndicatorDisplayString(macroName, emptyCommandName, c.commandType, t),
                    pageLink: `/profiles/edit/${profileId}`,
                });
            }
        });
    });

    return emptyCommands;
};

export const getDuplicateMacroCommands = (params: IMacroValidation): ProfileConflictItem[] => {
    const { macros, profileId, t } = params;
    const commands: CommandConflictDisplayModel[] = [];

    macros.forEach((macro) => {
        const nonEmptyCommands = macro.commands.filter((c) => c.command !== '');
        const transformedCommands = nonEmptyCommands.map((commandModel) => {
            let commandName = getCommandDisplayName(commandModel.command, t);
            if (commandModel.commandType === 'voice') {
                commandName = commandName.toLowerCase();
            }

            return {
                commandDisplayName: commandName,
                commandType: commandModel.commandType,
                macroOrKeyName: macro.name ?? getMacroFallbackName(commandModel.command, t),
                macroOrKeyId: macro.id,
                pageLink: `/profiles/edit/${profileId}`,
            };
        });
        commands.push(...transformedCommands);
    });

    return getDuplicateCommands(commands, 'DuplicateMacroInputCommands', t);
};

export const getUnreleasedLatchedKeys = (params: GetUnreleasedLatchedKeysParams): ProfileConflictItem[] => {
    const { macros, profileId, defaultLocale, t } = params;
    const unreleasedLatchedKeys: ProfileConflictItem[] = [];

    // Filter on macros with latched keys and those with release or toggle event key types
    const macrosWithLatchedKeys = macros.filter((macro) => macro.events.some((event) => event.isKeyLatch));
    const macrosWithReleaseKeys = macros.filter((macro) =>
        macro.events.some((event) => event.eventType === 'KeyRelease' || event.eventType === 'KeyToggle')
    );

    const stopAllMacros = macros.filter((macro) => macro.events.some((event) => event.eventType === 'StopOutputs'));

    // StopOutputs trumps all other key release types
    if (stopAllMacros.length === 0) {
        // Flatten release keys to an array of strings
        const releaseKeysPerMacro = macrosWithReleaseKeys.flatMap((macro) => macro.events.map((event) => event.keys));
        const releaseKeys = releaseKeysPerMacro.flat();

        // Iterate through each latched macro and find a release key
        macrosWithLatchedKeys.forEach((macro) => {
            macro.events.forEach((event) => {
                if (event.isKeyLatch && event.keys) {
                    for (const latchKey of event.keys) {
                        const hasReleasedKey = releaseKeys.length > 0 && releaseKeys.includes(latchKey);

                        if (!hasReleasedKey) {
                            const eventTypeDisplayName = t(getMacroEventDisplayType(event.eventType));
                            unreleasedLatchedKeys.push({
                                key: 'UnreleasedLatchedKey',
                                scrollID: macro.id,
                                displayString: getConflictIndicatorDisplayString(
                                    macro.name ?? getMacroFallbackName(macro.commands?.[0]?.command, t),
                                    getKeyDisplayname(latchKey, defaultLocale),
                                    eventTypeDisplayName,
                                    t
                                ),
                                pageLink: `/profiles/edit/${profileId}`,
                            });
                        }
                    }
                }
            });
        });
    }

    return unreleasedLatchedKeys;
};

export const getEmptyKeybindingCommands = (params: GetEmptyKeybindingCommandsParams): ProfileConflictItem[] => {
    const { commandKeyMappings, profileId, t } = params;
    const emptyCommands: ProfileConflictItem[] = [];
    const emptyCommandName = t('common:noName');

    commandKeyMappings.forEach((mapping) => {
        mapping.commands.forEach((c) => {
            if (!c.command) {
                emptyCommands.push({
                    key: 'EmptyKeybindingInputs',
                    scrollID: mapping.key,
                    displayString: getConflictIndicatorDisplayString(
                        mapping.displayName,
                        emptyCommandName,
                        c.commandType,
                        t
                    ),
                    pageLink: `/profiles/edit/${profileId}/advanced/keybindings`,
                });
            }
        });
    });

    return emptyCommands;
};

export const getDuplicateMacroNames = (params: IMacroValidation): ProfileConflictItem[] => {
    const { macros, profileId, t } = params;
    const uniqueNames = new Set<string>();
    const duplicatedNames: ProfileConflictItem[] = [];
    const noNameText = t('common:noName');

    macros.forEach((macro) => {
        const macroName = macro.name ?? getMacroFallbackName(macro.commands?.[0]?.command, t);
        if (macroName === noNameText) {
            return;
        }

        if (uniqueNames.has(macroName)) {
            duplicatedNames.push({
                displayString: macroName,
                key: 'DuplicateMacroName',
                pageLink: `/profiles/edit/${profileId}`,
                scrollID: macro.id,
            });
        } else {
            uniqueNames.add(macroName);
        }
    });

    return duplicatedNames;
};

export const getEmptyEvents = (params: IMacroValidation): ProfileConflictItem[] => {
    const { macros, profileId, t } = params;
    const emptyKeyConflicts: ProfileConflictItem[] = [];
    const eventTypeKey: MacroEventType[] = ['KeyPress', 'KeyRelease', 'KeyToggle'];
    const eventTypeTextInput: MacroEventType[] = ['Type', 'PlayAudio'];

    const isEmptyEvent = (event: MacroDisplayEvent): boolean => {
        const isKeyEventEmpty =
            eventTypeKey.includes(event.eventType) &&
            (!event.keys || event.keys.length === 0 || event.keys.some((key) => !key));

        const isFreeFormEventEmpty =
            eventTypeTextInput.includes(event.eventType) &&
            !(event.typedPhrase || event.outputSpeech || event.additionalInputContent);

        return isKeyEventEmpty || isFreeFormEventEmpty;
    };

    macros.forEach((macro) => {
        const macroName = macro.name ?? getMacroFallbackName(macro.commands[0]?.command, t);
        macro.events.forEach((event) => {
            const eventTypeDisplayName = t(getMacroEventDisplayType(event.eventType));
            if (isEmptyEvent(event)) {
                emptyKeyConflicts.push({
                    displayString: `${macroName}: ${eventTypeDisplayName}`,
                    key: 'EmptyEvent',
                    pageLink: `/profiles/edit/${profileId}`,
                    scrollID: macro.id,
                });
            }
        });
    });

    return emptyKeyConflicts;
};

/**
 * TRANSFORMERS AND OTHER HELPERS
 */
export const getMacroEventDisplayType = (eventType: MacroEventType) => {
    return macroEventTypes.find((macroDisplayType) => macroDisplayType.eventType === eventType)?.displayName;
};

export const keybindsFromDisplayModel = (keybinds: KeyCommandDisplayMapping[]): KeyCommandMapping[] => {
    return keybinds.map((k) => ({
        commands: k.commands.map((c) => c.command),
        key: k.key,
    }));
};

export const getAggregateKeybindings = (
    defaultKeybindings: KeyCommandDisplayMapping[],
    profileKeybindings: KeyCommandDisplayMapping[] = []
): KeyCommandDisplayMapping[] => {
    const profileBindingsMap = Object.fromEntries(profileKeybindings.map((k) => [k.key, k]));

    // Union of default and profile keybindings. Priority to profile keybindings, but following the order of default keybindings for consistency
    const unionKeybindings = defaultKeybindings.map((defaultKey) => profileBindingsMap[defaultKey.key] || defaultKey);

    if (!profileKeybindings?.length || profileKeybindings.every((k) => k.commands.length === 0)) {
        // no command keys or empty commands in profile keybindings
        return unionKeybindings.map((k) => ({ ...k, commands: [] }));
    }

    return unionKeybindings;
};

export const addNewProfileConflictModel = (
    title: string,
    items: ProfileConflictItem[],
    conflictList: ProfileConflictModel[],
    subtitle?: string
): void => {
    if (items.length > 0) {
        conflictList.push({
            title,
            items,
            subtitle,
        });
    }
};

/**
 * TRANSLATIONS AND NAMING
 */
export function getCommandDisplayName(command: string, t: TFunction): string {
    if (!command) return t('common:noName');
    if (command.startsWith('mouse_move'))
        return t('common:event.mouseMove.command', 'Move mouse', { command: command.replace('mouse_move', '') });
    const commandControl = allControls.find((c) => c.value === command);
    if (commandControl) {
        if (commandControl.isExternalKeyboard) {
            return commandControl.translationKey ?? commandControl.title;
        } else {
            return t(commandControl.title as CommonResourceKey);
        }
    }
    if (command.startsWith(hotkeyPrefix))
        return t('common:hotkey.tap', 'Tap', { value: command.replace(hotkeyPrefix, '') });
    return t('common:voice.say', 'Say', { value: command });
}

export const getMacroFallbackName = (command: string, t: TFunction): string => {
    if (!command) {
        return t('common:noName');
    }

    const commandControl = allControls.find((c) => c.value === command);

    if (commandControl) {
        if (commandControl.isExternalKeyboard) {
            return commandControl.translationKey ?? commandControl.title;
        } else {
            return t(commandControl.title ? (commandControl.title as CommonResourceKey) : 'common:noName');
        }
    }

    return command;
};

export const getConflictIndicatorDisplayString = (
    macroOrBindingName: string,
    commandOrKeyName: string,
    commandOrEventType: string,
    t: TFunction
): string => {
    return t(
        'common:profile.conflictIndicators.conflictIndicatorDisplayString',
        `${macroOrBindingName} : ${commandOrKeyName} with ${commandOrEventType}`,
        {
            container: macroOrBindingName,
            commandOrKeyName: commandOrKeyName,
            commandOrEventType: commandOrEventType,
        }
    );
};
